// src/components/ThemeCustomizer.tsx

import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
} from '@ionic/react';
import { checkmark } from 'ionicons/icons';
import { useState, useEffect, useRef } from 'react';
import { themeService, THEME_PRESETS, ThemePreset } from '../services/themeService';
import './ThemeCustomizer.css';

interface ThemeCustomizerProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const ThemeCustomizer: React.FC<ThemeCustomizerProps> = ({ isOpen, onDismiss }) => {
  const [selectedPreset, setSelectedPreset] = useState<ThemePreset>('default');
  const [originalPreset, setOriginalPreset] = useState<ThemePreset>('default');
  const hasSavedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      const current = themeService.getThemePreset();
      setSelectedPreset(current);
      setOriginalPreset(current);
      hasSavedRef.current = false;
    }
  }, [isOpen]);

  const handlePresetSelect = async (preset: ThemePreset) => {
    setSelectedPreset(preset);
    await themeService.setThemePreset(preset, { skipSave: true, skipEvent: true });
  };

  const handleCancel = async () => {
    if (selectedPreset !== originalPreset) {
      await themeService.setThemePreset(originalPreset);
    }
    onDismiss();
  };

  const handleSave = async () => {
    hasSavedRef.current = true;

    const currentIsDark = document.body.classList.contains('dark');
    const bgColor = currentIsDark ? '#111827' : '#ffffff';
    const spinnerColor = currentIsDark ? '#ffffff' : '#000000';
    const spinnerBorder = currentIsDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';

    const loadingDiv = document.createElement('div');
    loadingDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: ${bgColor};
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 99999;
    `;

    // Create spinner element programmatically (safer than innerHTML)
    const spinner = document.createElement('div');
    spinner.style.cssText = `
      width: 80px;
      height: 80px;
      border: 4px solid ${spinnerBorder};
      border-top-color: ${spinnerColor};
      border-radius: 50%;
      animation: spin 1s linear infinite;
    `;

    // Create style element for animation
    const style = document.createElement('style');
    style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';

    loadingDiv.appendChild(spinner);
    loadingDiv.appendChild(style);
    document.body.appendChild(loadingDiv);

    onDismiss();

    await new Promise(resolve => setTimeout(resolve, 100));

    await themeService.setThemePreset(selectedPreset);

    document.body.classList.add('theme-changing');
    void document.body.offsetHeight;
    await new Promise(resolve => setTimeout(resolve, 300));
    document.body.classList.remove('theme-changing');

    themeService.forceRefresh();

    await new Promise(resolve => setTimeout(resolve, 200));
    loadingDiv.remove();
  };

  const handleDidDismiss = async () => {
    if (!hasSavedRef.current && selectedPreset !== originalPreset) {
      await themeService.setThemePreset(originalPreset);
    }
  };

  const presetKeys = Object.keys(THEME_PRESETS) as ThemePreset[];

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleDidDismiss}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={handleCancel}>Cancel</IonButton>
          </IonButtons>
          <IonTitle>Choose Theme</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleSave} strong>
              Save
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent className="theme-customizer-content">
        <div className="ion-padding">
          <IonGrid>
            <IonRow>
              {presetKeys.map((preset) => {
                const themeData = THEME_PRESETS[preset];
                const hasRainbowColors = !!(themeData.colors.light.quaternary);
                return (
                  <IonCol size="6" key={preset}>
                    <IonCard
                      button
                      onClick={() => handlePresetSelect(preset)}
                      className={selectedPreset === preset ? 'theme-card-selected' : ''}
                    >
                      <IonCardHeader>
                        <IonCardTitle style={{ fontSize: '14px' }}>
                          {themeData.name}
                        </IonCardTitle>
                      </IonCardHeader>
                      <IonCardContent>
                        <div className="theme-preview-container">
                          <div className="theme-mode-label">Light</div>
                          <div className={hasRainbowColors ? 'theme-preview rainbow' : 'theme-preview'}>
                            <div
                              className="color-swatch"
                              style={{ backgroundColor: themeData.colors.light.primary }}
                            />
                            {hasRainbowColors && themeData.colors.light.quaternary && (
                              <div
                                className="color-swatch"
                                style={{ backgroundColor: themeData.colors.light.quaternary }}
                              />
                            )}
                            <div
                              className="color-swatch"
                              style={{ backgroundColor: themeData.colors.light.secondary }}
                            />
                            {hasRainbowColors && themeData.colors.light.quinary && (
                              <div
                                className="color-swatch"
                                style={{ backgroundColor: themeData.colors.light.quinary }}
                              />
                            )}
                            {hasRainbowColors && themeData.colors.light.senary && (
                              <div
                                className="color-swatch"
                                style={{ backgroundColor: themeData.colors.light.senary }}
                              />
                            )}
                            <div
                              className="color-swatch"
                              style={{ backgroundColor: themeData.colors.light.tertiary }}
                            />
                          </div>
                          <div className="theme-mode-label">Dark</div>
                          <div className={hasRainbowColors ? 'theme-preview rainbow' : 'theme-preview'}>
                            <div
                              className="color-swatch"
                              style={{ backgroundColor: themeData.colors.dark.primary }}
                            />
                            {hasRainbowColors && themeData.colors.dark.quaternary && (
                              <div
                                className="color-swatch"
                                style={{ backgroundColor: themeData.colors.dark.quaternary }}
                              />
                            )}
                            <div
                              className="color-swatch"
                              style={{ backgroundColor: themeData.colors.dark.secondary }}
                            />
                            {hasRainbowColors && themeData.colors.dark.quinary && (
                              <div
                                className="color-swatch"
                                style={{ backgroundColor: themeData.colors.dark.quinary }}
                              />
                            )}
                            {hasRainbowColors && themeData.colors.dark.senary && (
                              <div
                                className="color-swatch"
                                style={{ backgroundColor: themeData.colors.dark.senary }}
                              />
                            )}
                            <div
                              className="color-swatch"
                              style={{ backgroundColor: themeData.colors.dark.tertiary }}
                            />
                          </div>
                        </div>
                        {selectedPreset === preset && (
                          <IonIcon
                            icon={checkmark}
                            className="selected-icon"
                            color="primary"
                          />
                        )}
                      </IonCardContent>
                    </IonCard>
                  </IonCol>
                );
              })}
            </IonRow>
          </IonGrid>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default ThemeCustomizer;